/**
 * Data Bundles Management Page
 * Card-based layout with filter pills, unified list, and multi-select bulk sharing
 */

import React, { useState, useMemo } from 'react';
import { formatRelativeTime } from '@/utils/relativeTime';
import { message, Spin } from 'antd';
import { Sidebar } from '@/components/layout/Sidebar';
import { useLanguage } from '../../../hooks/useLanguage';
import { useAuth } from '../../../hooks/useAuth';
import { useFeature } from '@/hooks/useFeature';
import { useCachedFetch, invalidateCachedFetch } from '@/hooks/useCachedFetch';
import { DataBundleApi } from '../services/dataBundleService';
import { sharingService } from '@services/sharingService';
import { DataBundleModal } from '../components/DataBundleModal';
import { DataBundleViewModal } from '../components/DataBundleViewModal';
import { ShareModal } from '@/components/sharing/ShareModal';
import type { DataBundle } from '../types';
import presentIcon from '@/brand-assets/m2/General Icons/present.png';
import './DataBundlesPage.css';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return formatRelativeTime(date);
}

type FilterType = 'all' | 'mine' | 'shared';

export const DataBundlesPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useLanguage();
  const { user, isAdmin, isGroupAdmin, isSuperAdmin } = useAuth();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<DataBundle | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareBundleId, setShareBundleId] = useState('');
  const [shareBundleName, setShareBundleName] = useState('');
  const [shareEntityIds, setShareEntityIds] = useState<string[]>([]);
  const [viewBundle, setViewBundle] = useState<DataBundle | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const canShare = isAdmin || isGroupAdmin || isSuperAdmin;

  // SWR — instant paint on revisit.
  const bundlesKey = useMemo(() => ['bundles', user?.user_id] as const, [user?.user_id]);
  const { data: bundlesData, loading, refetch: reloadBundles } = useCachedFetch<DataBundle[]>(
    bundlesKey,
    async () => {
      const data = await DataBundleApi.listBundles();
      return data.data_bundles;
    },
  );
  const sharedBundlesKey = useMemo(() => ['shared-bundles', user?.user_id] as const, [user?.user_id]);
  const { data: sharedBundlesData, loading: sharedLoading } = useCachedFetch<DataBundle[]>(
    sharedBundlesKey,
    async () => {
      const data = await sharingService.getSharedEntities('data-bundles');
      return (data as DataBundle[]) || [];
    },
  );
  const bundles = bundlesData ?? [];
  const sharedBundles = sharedBundlesData ?? [];

  const loadBundles = () => {
    invalidateCachedFetch(bundlesKey as unknown as readonly (string|number|null|undefined)[]);
    return reloadBundles();
  };

  const handleShare = (bundle: DataBundle) => {
    setShareBundleId(bundle.data_bundle_id);
    setShareBundleName(bundle.bundle_name);
    setShareEntityIds([]);
    setShareModalVisible(true);
  };

  const handleBulkShare = () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setShareBundleId(ids[0]);
    setShareBundleName(
      ids.length === 1
        ? (bundles.find(b => b.data_bundle_id === ids[0])?.bundle_name || 'Bundle')
        : `${ids.length} bundles selected`
    );
    setShareEntityIds(ids);
    setShareModalVisible(true);
  };

  const handleShareModalClose = () => {
    setShareModalVisible(false);
    setShareEntityIds([]);
    setSelectedIds(new Set());
  };

  const handleCreate = () => {
    setSelectedBundle(undefined);
    setModalVisible(true);
  };

  const handleEdit = (bundle: DataBundle) => {
    setSelectedBundle(bundle);
    setModalVisible(true);
  };

  const handleDelete = async (bundleId: string) => {
    if (!window.confirm(t('dataBundles.confirmDelete'))) {
      return;
    }
    try {
      await DataBundleApi.deleteBundle(bundleId);
      message.success(t('dataBundles.deleteSuccess'));
      loadBundles();
    } catch (error: any) {
      message.error(t('dataBundles.deleteError'));
      console.error('Error deleting data bundle:', error);
    }
  };

  const handleModalSave = () => {
    setModalVisible(false);
    setSelectedBundle(undefined);
    loadBundles();
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedBundle(undefined);
  };

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    setSelectedIds(new Set());
  };

  const handleSelectToggle = (bundleId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  };

  const handleCancelSelection = () => {
    setSelectedIds(new Set());
  };

  // Apply search filter to both own and shared bundles
  const filterBySearch = (list: DataBundle[]) => {
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(b =>
      b.bundle_name.toLowerCase().includes(term) ||
      (b.bundle_description || '').toLowerCase().includes(term)
    );
  };

  const filteredOwnBundles = useMemo(() => filterBySearch(bundles), [bundles, searchTerm]);
  const filteredSharedBundles = useMemo(() => filterBySearch(sharedBundles), [sharedBundles, searchTerm]);

  // Build the display list based on active filter
  const displayBundles = useMemo(() => {
    switch (activeFilter) {
      case 'mine':
        return filteredOwnBundles.map(b => ({ bundle: b, isShared: false }));
      case 'shared':
        return filteredSharedBundles.map(b => ({ bundle: b, isShared: true }));
      case 'all':
      default:
        return [
          ...filteredOwnBundles.map(b => ({ bundle: b, isShared: false })),
          ...filteredSharedBundles.map(b => ({ bundle: b, isShared: true })),
        ];
    }
  }, [activeFilter, filteredOwnBundles, filteredSharedBundles]);

  const isFullyEmpty = bundles.length === 0 && sharedBundles.length === 0;
  const libraryEnabled = useFeature('modules.library');

  const bundlesInner = (
    <>
          {/* Header */}
          <div className="simple-chat-header">
            <h1 dir="auto" className="bot-title">{t('dataBundles.title')}</h1>
            <p className="text-muted">{t('dataBundles.pageSubtitle')}</p>
          </div>

          {/* On a Library tenant these bundles are kept VISIBLE on purpose:
              hiding the tab read as "my bundles are gone", which is the
              opposite of the truth — they were migrated into the Library and
              are kept for rollback. Say where the files live now; the server
              refuses bundle writes on Library tenants regardless, so looking
              here can never diverge from the Library. */}
          {libraryEnabled && (
            <div className="bundles-library-note">
              {t('dataBundles.libraryNote')}
            </div>
          )}

          {/* Search row: search left, create right */}
          <div className="bundles-controls">
            <div className="bundles-search-box">
              <svg className="bundles-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input dir="auto"
                type="text"
                className="bundles-search-input"
                placeholder={t('admin.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="bundles-create-btn" onClick={handleCreate}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              {t('dataBundles.create')}
            </button>
          </div>

          {/* Filter Pills */}
          <div className="bundles-filter-pills">
            <button
              className={`bundles-filter-pill ${activeFilter === 'all' ? 'bundles-filter-pill--active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              {t('common.filterAll')} ({filteredOwnBundles.length + filteredSharedBundles.length})
            </button>
            <button
              className={`bundles-filter-pill ${activeFilter === 'mine' ? 'bundles-filter-pill--active' : ''}`}
              onClick={() => handleFilterChange('mine')}
            >
              {t('common.filterMine')} ({filteredOwnBundles.length})
            </button>
            <button
              className={`bundles-filter-pill ${activeFilter === 'shared' ? 'bundles-filter-pill--active' : ''}`}
              onClick={() => handleFilterChange('shared')}
            >
              {t('common.filterShared')} ({filteredSharedBundles.length})
            </button>
          </div>

          {/* Bundles List */}
          <div className="bundles-list-container">
            {(loading || sharedLoading) ? (
              <div className="bundles-loading">
                <Spin size="large" />
              </div>
            ) : isFullyEmpty ? (
              /* Full empty state: no bundles at all */
              <div className="bundles-empty">
                <div className="bundles-empty-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <h3>{t('dataBundles.noBundlesYet')}</h3>
                <p>{t('dataBundles.noBundlesYetHint')}</p>
                <button className="bundles-create-btn" onClick={handleCreate}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  {t('dataBundles.create')}
                </button>
              </div>
            ) : displayBundles.length === 0 && searchTerm ? (
              /* No search results */
              <div className="bundles-inline-empty">
                <span>{t('dataBundles.noBundlesMatchSearch')}</span>
              </div>
            ) : displayBundles.length === 0 && activeFilter === 'mine' ? (
              /* Mine filter empty, but shared exist */
              <div className="bundles-inline-empty">
                <span>{t('dataBundles.youHaveNoBundles')}</span>
                <button className="bundles-inline-create-link" onClick={handleCreate}>
                  {t('dataBundles.create')}
                </button>
              </div>
            ) : displayBundles.length === 0 && activeFilter === 'shared' ? (
              /* Shared filter empty */
              <div className="bundles-inline-empty">
                <span>{t('dataBundles.noBundlesSharedWithYou')}</span>
              </div>
            ) : (
              <div className="bundles-section">
                {displayBundles.map(({ bundle, isShared }) => (
                  <BundleCard
                    key={isShared ? `shared-${bundle.data_bundle_id}` : bundle.data_bundle_id}
                    bundle={bundle}
                    onEdit={isShared ? () => {} : handleEdit}
                    onDelete={isShared ? () => {} : handleDelete}
                    onShare={(!isShared || bundle.can_reshare) ? handleShare : undefined}
                    onView={(b) => setViewBundle(b)}
                    canShare={(!isShared || !!bundle.can_reshare) && canShare}
                    isShared={isShared}
                    selectable={(!isShared || !!bundle.can_reshare) && canShare}
                    selected={selectedIds.has(bundle.data_bundle_id)}
                    onSelectToggle={handleSelectToggle}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sticky bottom action bar for bulk selection */}
          {selectedIds.size > 0 && (
            <div className="bundles-bulk-bar">
              <span className="bundles-bulk-count">{t('common.nSelected', { count: selectedIds.size })}</span>
              <div className="bundles-bulk-actions">
                <button className="bundles-bulk-btn bundles-bulk-share" onClick={handleBulkShare}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"/>
                    <circle cx="6" cy="12" r="3"/>
                    <circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                  </svg>
                  {t('sharing.title')}
                </button>
                <button className="bundles-bulk-btn bundles-bulk-cancel" onClick={handleCancelSelection}>
                  {t('vault.cancel')}
                </button>
              </div>
            </div>
          )}
    </>
  );

  const bundlesModals = (
    <>
      <DataBundleModal
        visible={modalVisible}
        bundle={selectedBundle}
        onSave={handleModalSave}
        onCancel={handleModalCancel}
      />

      <ShareModal
        isOpen={shareModalVisible}
        onClose={handleShareModalClose}
        entityType="data-bundles"
        entityId={shareBundleId}
        entityName={shareBundleName}
        entityIds={shareEntityIds.length > 0 ? shareEntityIds : undefined}
      />

      <DataBundleViewModal
        visible={!!viewBundle}
        bundle={viewBundle}
        onClose={() => setViewBundle(null)}
      />
    </>
  );

  // Embedded mode (rendered as a tab inside the Agents page): no Sidebar / page
  // chrome — the body sits directly in the host's content column.
  if (embedded) {
    return <>{bundlesInner}{bundlesModals}</>;
  }

  return (
    <div className="admin-container">
      <Sidebar />
      <div className="main-container">
        <main className="main-content">{bundlesInner}</main>
      </div>
      {bundlesModals}
    </div>
  );
};

/* -- Bundle Card Component -- */

interface BundleCardProps {
  bundle: DataBundle;
  onEdit: (bundle: DataBundle) => void;
  onDelete: (bundleId: string) => void;
  onShare?: (bundle: DataBundle) => void;
  onView?: (bundle: DataBundle) => void;
  canShare?: boolean;
  isShared?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectToggle?: (bundleId: string) => void;
}

const BundleCard: React.FC<BundleCardProps> = ({
  bundle,
  onEdit,
  onDelete,
  onShare,
  onView,
  canShare,
  isShared,
  selectable,
  selected,
  onSelectToggle,
}) => {
  const { t } = useLanguage();
  const fileCount = bundle.file_count || 0;
  const syncFolderCount = bundle.sync_folders?.length || 0;

  return (
    <div className={`bundle-card-row ${selected ? 'bundle-card-row--selected' : ''}`}>
      {selectable && (
        <label className="bundle-card-checkbox" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onSelectToggle?.(bundle.data_bundle_id)}
          />
          <span className="bundle-card-checkbox-visual" />
        </label>
      )}

      <img src={presentIcon} alt="" className="bundle-card-icon" />

      <div className="bundle-card-info">
        <div className="bundle-card-name" dir="auto">{bundle.bundle_name}</div>
        {bundle.bundle_description && (
          <div className="bundle-card-description bidi-clamp" dir="auto">{bundle.bundle_description}</div>
        )}
        <div className="bundle-card-meta">
          <span className="bundle-card-date">
            {formatDate(bundle.created_at)}
          </span>
          {isShared && (
            <span className="bundle-card-shared-badge">
              {t('common.shared')}
            </span>
          )}
        </div>
      </div>

      <div className="bundle-card-labels">
        {fileCount > 0 && (
          <span className="bundle-card-label bundle-card-label-files">
            {t('counts.files', { count: fileCount })}
          </span>
        )}
        {syncFolderCount > 0 && (
          <span className="bundle-card-label bundle-card-label-sync">
            {t('counts.syncFolders', { count: syncFolderCount })}
          </span>
        )}
      </div>

      <div className="bundle-card-actions">
        {onView && (
          <button
            className="bundle-action-btn"
            onClick={() => onView(bundle)}
            title={t('dataBundles.viewDetails')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        )}
        {canShare && onShare && (
          <button
            className="bundle-action-btn"
            onClick={() => onShare(bundle)}
            title={t('dataBundles.shareBundle')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
        )}
        {!isShared && (
          <>
            <button
              className="bundle-action-btn"
              onClick={() => onEdit(bundle)}
              title={t('dataBundles.editBundle')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              className="bundle-action-btn bundle-action-danger"
              onClick={() => onDelete(bundle.data_bundle_id)}
              title={t('dataBundles.deleteBundle')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
